// The waytide:// deep-link TS listener (Brief 4 / P6 — the half-wired gap).
//
// The Rust side already registers the scheme (tauri-plugin-deep-link); this is
// the missing TS half: it listens for an opened deep link (both cold-start and
// while-running) and routes it to the session channel. Desktop-only and the
// plugin is dynamically imported, so the browser build never pulls a Tauri API.
//
// URL grammar (named here, as P6 asks):
//   waytide://thread/<id>   → open that session's channel
// A share/invite link rides the existing https://…/c/<token> lane
// (ShareLandingPage), so it is intentionally NOT duplicated here.

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { isDesktopShell } from "./desktopBridge";

export interface DeepLinkTarget {
  threadId: number;
}

/** Parse a waytide:// deep link to its target, or null when it is not a link we
 * route. Pure — the grammar is unit-tested without Tauri. Tolerant of the two
 * shapes a custom-scheme URL parses into (`host` vs leading path segment). */
export function parseDeepLink(url: string): DeepLinkTarget | null {
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return null;
  }
  if (u.protocol !== "waytide:") return null;
  const segments = `${u.host}${u.pathname}`
    .split("/")
    .map((s) => s.trim())
    .filter(Boolean);
  // segments: ["thread", "<id>"]
  if (segments[0] === "thread" && segments[1] != null) {
    const threadId = Number(segments[1]);
    if (Number.isFinite(threadId)) return { threadId };
  }
  return null;
}

/** Subscribe to opened deep links (desktop only) and route them to the channel.
 * Handles the cold-start URL and live opens; cleans up its listener. */
export function useDeepLink(onThread: (threadId: number) => void): void {
  useEffect(() => {
    if (!isDesktopShell()) return;
    let unlisten: (() => void) | undefined;
    let alive = true;

    void (async () => {
      try {
        const { onOpenUrl, getCurrent } = await import(
          "@tauri-apps/plugin-deep-link"
        );
        const route = (urls: string[] | null) => {
          for (const url of urls ?? []) {
            const target = parseDeepLink(url);
            if (target) onThread(target.threadId);
          }
        };
        route(await getCurrent()); // the link the app was cold-started with
        const stop = await onOpenUrl(route); // links while running
        if (alive) unlisten = stop;
        else stop();
      } catch {
        // No deep-link plugin at runtime (e.g. a stale build) — stay quiet.
      }
    })();

    return () => {
      alive = false;
      unlisten?.();
    };
  }, [onThread]);
}

/** Mounts the deep-link listener inside the router and routes a link to the
 * channel surface with that thread preselected. Renders nothing. */
export function DeepLinkBridge(): null {
  const navigate = useNavigate();
  useDeepLink((threadId) => navigate(`/channels?thread=${threadId}`));
  return null;
}
